import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAppId } from '../context/AppIdContext';
import '../style/speakSearch.css';
import Header from '../component/header';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const VolunteersGroup = () => {
    // --- State ---
    const [volunteers, setVolunteers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");

    // --- Hooks ---
    const navigate = useNavigate();

    // --- Context ---
    const { appId, language, userId } = useAppId();
    const lang = language || 'en';

    // --- Translation Dictionary ---
    const t = {
        en: {
            pageTitle: "Volunteer's Group",
            pageSubtitle: "Overview of all volunteer groups and their total member reach.",
            filterPlaceholder: "Search by name, role or mobile...",
            loadingData: "Loading Data...",
            noData: "No volunteers found.",
            call: "Call",
            voterLabel: "Voter :",
            totalSum: "Total Voters :",
            volFound: "volunteers found"
        },
        hi: {
            pageTitle: "स्वयंसेवक समूह",
            pageSubtitle: "सभी स्वयंसेवक समूहों और उनकी कुल सदस्य पहुंच का विवरण।",
            filterPlaceholder: "नाम, पद या मोबाइल द्वारा खोजें...",
            loadingData: "डेटा लोड हो रहा है...",
            noData: "कोई स्वयंसेवक नहीं मिला।",
            call: "कॉल",
            voterLabel: "मतदाता :",
            totalSum: "कुल मतदाता :",
            volFound: "स्वयंसेवक मिले"
        },
        gu: {
            pageTitle: "સ્વયંસેવકનું જૂથ",
            pageSubtitle: "તમામ સ્વયંસેવક જૂથો અને તેમના કુલ સભ્યોની ઝાંખી.",
            filterPlaceholder: "નામ, ભૂમિકા અથવા મોબાઇલથી શોધો...",
            loadingData: "ડેટા લોડ થઈ રહ્યો છે...",
            noData: "કોઈ સ્વયંસેવક મળ્યા નથી.",
            call: "કૉલ",
            voterLabel: "મતદાર :",
            totalSum: "કુલ મતદારો :",
            volFound: "સ્વયંસેવકો મળ્યા"
        }
    };

    const txt = t[lang];

    // --- API Fetch Logic ---
    const fetchVolunteers = async () => {
        if (!appId || !userId) return;

        setLoading(true);
        setSearchTerm("");

        try {
            const response = await axios.post(
                `${BASE_URL}/api/dis_user_list_by_create_group`,
                {
                    app_id: appId,
                    user_id: userId
                }
            );

            if (response.data.success && Array.isArray(response.data.data.result)) {
                setVolunteers(response.data.data.result);
            } else {
                setVolunteers([]);
            }
        } catch (error) {
            console.error("Error fetching volunteers:", error);
            setVolunteers([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (appId) {
            fetchVolunteers();
        }
    }, [appId, userId]);

    // --- Filter Logic ---
    const filteredVolunteers = volunteers.filter((vol) => {
        if (!searchTerm) return true;
        const term = searchTerm.toLowerCase();

        const name = vol.name ? vol.name.toLowerCase() : "";
        const mobile = vol.mobile_no ? String(vol.mobile_no) : "";
        const desig = vol.designation ? vol.designation.toLowerCase() : "";
        const type = vol.user_type === 'A' ? 'admin' : (vol.user_type === 'k' ? 'karyakarta' : 'volunteer');

        return name.includes(term) || mobile.includes(term) || desig.includes(term) || type.includes(term);
    });

    // --- Calculate Total Members ---
    const totalMemberCount = filteredVolunteers.reduce((sum, vol) => {
        return sum + (parseInt(vol.total_member) || 0);
    }, 0);

    // --- Navigation Handler ---
    const handleCardClick = (selectedUserId , userName) => {
        navigate('/dis_my_group_member', { state: { userId: selectedUserId , userName:userName } });
    };

    return (
        <div className='app-container p-4'>

            {/* 1. Header Integration with Stats Badges */}
            <Header
                title={txt.pageTitle}
            />

            {/* 2. Professional Filter Container (Always Visible) */}
            <div className="filter-container-modern">
                <div className="search-wrapper-modern">
                    <svg className="search-icon-modern" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <input
                        className="search-input-modern"
                        type="text"
                        placeholder={txt.filterPlaceholder}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
            </div>

            {/* Loading Spinner */}
            {loading && (
                <div className="loader-wrapper">
                    <div className="spinner-modern"></div>
                    <p className="loader-text">{txt.loadingData}</p>
                </div>
            )}

            {/* No Data State */}
            {!loading && filteredVolunteers.length === 0 && (
                <div className="text-center text-muted p-5 empty-state-modern">
                    <div className="empty-icon">👥</div>
                    <p>{txt.noData}</p>
                </div>
            )}

            {/* --- VOLUNTEER LIST GRID --- */}
            <div className="row g-4 pb-5">
                {!loading && filteredVolunteers.map((vol, index) => {
                    const uniqueKey = vol.user_id ? `vol-group-${vol.user_id}` : `vol-group-${index}`;
                    const userTypeLabel = vol.designation || (vol.user_type === 'A' ? 'Admin' : (vol.user_type === 'k' ? 'Karyakarta' : 'User'));

                    let badgeClass = 'badge-blue';
                    if (vol.user_type === 'A' || userTypeLabel.toLowerCase().includes('admin')) {
                        badgeClass = 'badge-red';
                    } else if (vol.user_type === 'k' || userTypeLabel.toLowerCase().includes('karya')) {
                        badgeClass = 'badge-orange';
                    }

                    let avatarBg = '#e0f2fe'; let avatarText = '#0284c7';
                    if (badgeClass === 'badge-red') { avatarBg = '#fee2e2'; avatarText = '#dc2626'; }
                    else if (badgeClass === 'badge-orange') { avatarBg = '#fef3c7'; avatarText = '#d97706'; }

                    return (
                        <div className="col-12 col-md-6 col-lg-4 col-xl-3" key={uniqueKey}>
                            <div
                                className="volunteer-card-web-modern"
                                onClick={() => handleCardClick(vol.user_id, vol.name)}
                            >
                                <div className="card-top-flex">
                                    <div className="card-avatar-wrapper-modern">
                                        {vol.photo ? (
                                            <img src={vol.photo} alt="user" className="card-avatar-img" onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex' }} />
                                        ) : null}
                                        <div className="card-avatar-placeholder" style={{ display: vol.photo ? 'none' : 'flex', backgroundColor: avatarBg, color: avatarText }}>
                                            {vol.name ? vol.name.charAt(0).toUpperCase() : "U"}
                                        </div>
                                    </div>
                                    <div className="card-middle-modern">
                                        <h3 className="card-title-name-modern text-truncate">
                                            {index + 1}. {vol.name || "Unknown"}
                                        </h3>
                                        <div className="card-meta-row-modern">
                                            <span className={`role-badge-modern ${badgeClass}`}>
                                                {userTypeLabel}
                                            </span>
                                        </div>
                                    </div>
                                    <button
                                        className="btn-call-circle-modern"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            window.open(`tel:${vol.mobile_no}`);
                                        }}
                                        disabled={!vol.mobile_no}
                                        title={txt.call}
                                    >
                                        <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24">
                                            <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" />
                                        </svg>
                                    </button>
                                </div>
                                <div className="card-bottom-row-modern">
                                    <span className="voter-count-label-modern">
                                        {txt.voterLabel} <strong>{vol.total_member || 0}</strong>
                                    </span>
                                    <span className="view-link-modern">View Group →</span>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            <style jsx>{`
                /* --- FILTER BAR --- */
                .filter-container-modern { display: flex; justify-content: flex-start; align-items: center; margin-bottom: 24px; }
                .search-wrapper-modern { position: relative; width: 100%; max-width: 450px; }
                .search-icon-modern { position: absolute; left: 14px; top: 50%; transform: translateY(-50%); color: #94a3b8; width: 20px; height: 20px; z-index: 1; }
                .search-input-modern { width: 100%; padding: 12px 16px 12px 42px; border: 1px solid #e2e8f0; border-radius: 12px; font-size: 14px; color: #1e293b; outline: none; transition: all 0.2s ease; box-shadow: 0 1px 2px rgba(0,0,0,0.05); }
                .search-input-modern:focus { border-color: #2563eb; box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1); }

                /* --- BADGES & STATS --- */
                .badge-stat { background: #f1f5f9; color: #475569; padding: 4px 12px; border-radius: 20px; font-weight: 700; font-size: 0.85rem; border: 1px solid #e2e8f0; }
                .badge-stat.success { background: #dcfce7; color: #15803d; border-color: #bbf7d0; }

                /* --- CARDS --- */
                .volunteer-card-web-modern { background: white; border: 1px solid #e5e7eb; border-radius: 16px; padding: 16px; cursor: pointer; transition: all 0.2s ease; height: 100%; display: flex; flex-direction: column; }
                .volunteer-card-web-modern:hover { transform: translateY(-4px); border-color: #2563eb; box-shadow: 0 10px 15px -3px rgba(37, 99, 235, 0.1); }
                .card-top-flex { display: flex; align-items: center; gap: 14px; margin-bottom: 16px; }
                .card-avatar-wrapper-modern { width: 50px; height: 50px; border-radius: 50%; overflow: hidden; flex-shrink: 0; }
                .card-avatar-placeholder { width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; font-weight: 800; font-size: 1.2rem; }
                .card-avatar-img { width: 100%; height: 100%; object-fit: cover; }
                .card-middle-modern { flex: 1; min-width: 0; }
                .card-title-name-modern { font-size: 15px; font-weight: 700; color: #0f172a; margin: 0 0 4px; }
                .role-badge-modern { font-size: 10px; font-weight: 700; padding: 2px 8px; border-radius: 4px; text-transform: uppercase; color: white; display: inline-block; }
                .badge-red { background-color: #ef4444; }
                .badge-orange { background-color: #f59e0b; }
                .badge-blue { background-color: #3b82f6; }

                .btn-call-circle-modern { width: 36px; height: 36px; border-radius: 50%; border: none; background: #f0f9ff; color: #0369a1; cursor: pointer; transition: 0.2s; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
                .btn-call-circle-modern:hover:not(:disabled) { background: #0ea5e9; color: white; transform: scale(1.1); }

                .card-bottom-row-modern { border-top: 1px dashed #e2e8f0; padding-top: 12px; display: flex; justify-content: space-between; align-items: center; margin-top: auto; }
                .voter-count-label-modern { font-size: 13px; color: #64748b; font-weight: 500; }
                .voter-count-label-modern strong { color: #0f172a; font-weight: 700; }
                .view-link-modern { font-size: 12px; color: #2563eb; font-weight: 700; }

                /* --- LOADER & EMPTY STATE --- */
                .loader-wrapper { display: flex; flex-direction: column; justify-content: center; align-items: center; min-height: 40vh; width: 100%; }
                .spinner-modern { width: 40px; height: 40px; border: 3px solid #e2e8f0; border-top: 3px solid #2563eb; border-radius: 50%; animation: spin 0.8s linear infinite; margin-bottom: 12px; }
                .empty-state-modern { text-align: center; padding: 60px 20px; background: white; border-radius: 20px; border: 2px dashed #e2e8f0; color: #94a3b8; width: 100%; }
                .empty-icon { font-size: 40px; margin-bottom: 12px; }
                @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }

                @media (max-width: 640px) {
                    .search-wrapper-modern { max-width: 100%; }
                }
            `}</style>
        </div>
    );
};

export default VolunteersGroup;